import AdminHeader from "components/Headers/AdminHeader";
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  UncontrolledTooltip,
  Input,
  Badge,
} from "reactstrap";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import {
  showAlert,
  formatDate,
} from "components/CommonComponents/CommonFunctions";
import { useDispatch, useSelector } from "react-redux";
import { questionBankList } from "constants/questionBank";
import { getTagList } from "action/tags";
import LanguageModal from "./LanguageModal";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import {
  addQuestionBank,
  updateQuestionBank,
  getQuestionBankList,
} from "action/questionBank";
import DatePicker from "react-datepicker";

const QuestionBankList = () => {
  const { notify } = useContext(NotificationContext);
  const dispatch = useDispatch();
  const tagsData = useSelector((state) => state.tags.tagsList);
  const questionBankData = useSelector(
    (state) => state.questionBanks.questionBankList,
  );
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    name: "",
    tag: null,
    status: null,
    createdOn: [null, null],
    filterParams: [],
  });
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [loaderOnClick, setLoaderOnClick] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    getTagList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    getQuestionBankList(dispatch, pageNum, searchState.filterParams);
    return () => {
      dispatch({ type: questionBankList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (questionBankData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: questionBankData?.data?.QuestionBank?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (questionBankData?.code || questionBankData?.message) {
      setListState({
        isLoading: false,
        error: questionBankData?.message || "Failed to load question bank list",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [questionBankData]);

  const tagOptions =
    tagsData?.data?.Tag?.filter((tag) => tag.status === "ACTIVE").map(
      (tag) => ({
        value: tag.ID,
        label: tag.name,
      }),
    ) || [];

  const buildFilterParams = () => {
    const filters = [];

    if (searchState.name?.trim()) {
      filters.push({
        field: "Name",
        condition: "like",
        filterValues: [searchState.name.trim()],
      });
    }

    if (searchState.tag?.value) {
      filters.push({
        field: "TagName",
        condition: "eq",
        filterValues: [searchState.tag.label],
      });
    }

    if (searchState.status?.value) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }

    if (searchState.createdOn[0]) {
      if (searchState.createdOn[1]) {
        filters.push({
          field: "CreatedAt",
          condition: "btw",
          filterValues: [
            formatDate(searchState.createdOn[0]),
            formatDate(searchState.createdOn[1]),
          ],
        });
      } else {
        filters.push({
          field: "CreatedAt",
          condition: "eq",
          filterValues: [formatDate(searchState.createdOn[0])],
        });
      }
    }

    return filters.length ? JSON.stringify(filters) : [];
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !== JSON.stringify(newFilters)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilters,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      name: "",
      tag: null,
      status: null,
      createdOn: [null, null],
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const formattedData =
    questionBankData?.data?.QuestionBank?.map((item, index) => ({
      id: item.ID,
      questionBankName: item.bankName,
      tag: item.tagName,
      status: item.status,
      createdAt: item.createdAt,
      srNo:
        (pageNum - 1) *
          (questionBankData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) || [];

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleLanguageSave = async (data) => {
    const bodyData = {
      name: data.name,
      language: data.language,
      tagID: data.tagID,
      status: "ACTIVE",
    };

    try {
      setLoaderOnClick(true);
      const res = await addQuestionBank(bodyData);

      if (res?.code === 200) {
        const questionBankID = res?.data?.questionBankID;
        const encodedId = urlEncodedId(questionBankID);

        notify("success", res?.message || "Question bank added successfully");
        setIsLanguageModalOpen(false);

        navigate(
          role === "SUPER_ADMIN"
            ? `/super-admin/question-bank/add/${encodedId}`
            : `/admin/question-bank/add/${encodedId}`,
        );
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to add question bank";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to add question bank");
    } finally {
      setLoaderOnClick(false);
    }
  };

  const handleOnRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.id); //rowData.ID
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/question-bank/${encodedId}`
        : `/admin/question-bank/${encodedId}`,
    );
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const toggleStatus = (question) => {
  const { status, id } = question;

  const isActive = status === "ACTIVE";
  const newStatus = isActive ? "INACTIVE" : "ACTIVE";

  showAlert({
    title: `Are you sure you want to ${
      isActive ? "inactivate" : "activate"
    } this question bank?`,
    type: "warning",
    confirmStyle: "deep-blue",
    cancelStyle: "secondary",
    confirmText: "Yes",
    cancelText: "Cancel",
    onConfirm: async (closeAlert) => {
      closeAlert(); // Close confirmation popup
      setIsUpdatingStatus(true);

      try {
        const payload = {
          status: newStatus,
        };

        const res = await updateQuestionBank(id, payload);

        if (res?.code === 200) {
          notify(
            "success",
            res?.message ||
              `Question bank ${newStatus.toLowerCase()} successfully`,
          );

          await getQuestionBankList(
            dispatch,
            pageNum,
            searchState.filterParams,
          );
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
            : res?.message || "Failed to update status";

          notify("danger", message);
        }
      } catch (error) {
        notify("danger", error?.message || "Something went wrong!");
      } finally {
        setIsUpdatingStatus(false);
      }
    },
  });
};
  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      Header: "Question Bank Name",
      accessor: "questionBankName",
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Tag",
      accessor: "tag",
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Created On",
      accessor: "createdAt",
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Status",
      accessor: "status",
      minWidth: 60,
      maxWidth: 60,
      Cell: ({ value }) => (
        <span
          className={`badge ${
            value === "ACTIVE" ? "badge-success" : "badge-danger"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      Header: "Actions",
      disableSortBy: true,
      minWidth: 90,
      maxWidth: 90,
      Cell: ({ row }) => {
        const { id, status } = row.original;
        const isActive = status === "ACTIVE";

        return (
          <div
            className={`d-flex align-items-center gap-2 ${
              !isActive ? "inactive-action-spacing" : ""
            }`}
          >
            {/* View */}
            <Button
              className="btn-link p-0"
              id={`view${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const encodedId = urlEncodedId(id);

                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/question-bank/${encodedId}`
                    : `/admin/question-bank/${encodedId}`,
                );
              }}
            >
              <i className="fas fa-eye" />
            </Button>
            <UncontrolledTooltip target={`view${id}`}>View</UncontrolledTooltip>

            {/* Edit */}
            {isActive && (
              <>
                <Button
                  className="btn-link p-0"
                  id={`edit${id}`}
                  size="sm"
                  disabled={!isActive}
                  onClick={(e) => {
                    e.stopPropagation();
                    const encodedId = urlEncodedId(id);

                    navigate(
                      role === "SUPER_ADMIN"
                        ? `/super-admin/question-bank/edit/${encodedId}`
                        : `/admin/question-bank/edit/${encodedId}`,
                      {
                        state: {
                          mode: "edit",
                          questionBank: row.original,
                        },
                      },
                    );
                  }}
                >
                  <i className="fas fa-edit" />
                </Button>
                <UncontrolledTooltip target={`edit${id}`}>
                  Edit
                </UncontrolledTooltip>
              </>
            )}

            <span className="mr-1 text-muted">|</span>

            <Button
              color="link"
              size="lg"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${id}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(row.original);
              }}
            >
              <i
                className={`fas status-toggle-icon ${
                  isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${id}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <AdminHeader />
      {isUpdatingStatus && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Updating status... Please wait.
            </div>
          </div>
        </div>
      )}

      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              {/* HEADER */}
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="text-default mb-0">Question Banks</h3>

                <Button
                  className="btn-deep-blue"
                  onClick={() => setIsLanguageModalOpen(true)}
                >
                  <i className="fa fa-plus mr-1" />
                  Question Bank
                </Button>
              </CardHeader>

              {/* BODY */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by question bank name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.name}
                      onKeyDown={handleKeyDown}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <DatePicker
                        onChangeRaw={(e) => e.preventDefault()}
                        selected={searchState.createdOn[0]}
                        onChange={(dates) =>
                          setSearchState((prev) => ({
                            ...prev,
                            createdOn: dates,
                          }))
                        }
                        startDate={searchState.createdOn[0]}
                        endDate={searchState.createdOn[1]}
                        selectsRange
                        onKeyDown={handleKeyDown}
                        isClearable={!!searchState.createdOn[0]}
                        placeholderText="Select created on date or range"
                        className="form-control mb-2 filter-input-large"
                        dateFormat="dd-MMM-yyyy"
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        calendarClassName="custom-calendar"
                        yearDropdownItemNumber={100}
                      />
                    </div>

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={tagOptions}
                      onKeyDown={handleKeyDown}
                      placeholder="Select tag"
                      value={searchState.tag}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          tag: opt,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={STATUS_OPTIONS}
                      onKeyDown={handleKeyDown}
                      components={{ Option, SingleValue }}
                      placeholder="Select status"
                      value={searchState.status}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          status: opt,
                        }))
                      }
                    />

                    <Button
                      color="default"
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
                      onClick={handleSearch}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {searchState.filterParams.length > 0 && (
                      <Button
                        className="mb-2 btn-deep-blue filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {questionBankData?.data?.totalRecords > 0 ? (
                    <>
                      {searchState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {questionBankData?.data?.totalFilteredRecords}{" "}
                          of {questionBankData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {questionBankData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                <div className="table-responsive">
                  {listState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : listState.error ? (
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                      <span>{listState.error}</span>
                    </div>
                  ) : listState.noRecords ? (
                    <div className="loaderContainer empty-style">
                      <div className="empty-icon">
                        <i class="fa-solid fa-circle-info"></i>
                      </div>
                      <span> No question banks found.</span>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <CommonTable
                        columns={columns}
                        data={formattedData}
                        pageNum={pageNum}
                        setPageNum={setPageNum}
                        defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                        showPaginationBottom
                        totalPages={Math.ceil(
                          questionBankData?.data?.totalFilteredRecords /
                            questionBankData?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE,
                        )}
                        rowClick={handleOnRowClick}
                      />
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <LanguageModal
        isOpen={isLanguageModalOpen}
        toggleModal={() => setIsLanguageModalOpen(false)}
        onSave={handleLanguageSave}
        loaderOnClick={loaderOnClick}
      />
    </>
  );
};

export default QuestionBankList;
